/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { Dropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Label } from '@fluentui/react/lib/Label';
import { IPersonaProps } from '@fluentui/react/lib/Persona';
import { ResponsiveMode } from '@fluentui/react/lib/ResponsiveMode';
import { Stack } from '@fluentui/react/lib/Stack';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { CustomizerContext } from '@fluentui/react/lib/Utilities';
import * as React from 'react';

import { DetailsTextField, TitleTextField } from '../../..';
import { TemplateScope } from '../../../../../apollo/reactiveVariables/TemplateManagementVariables';
import { Constants } from '../../../../../common/Constants';
import { ApprovalPersonaProps } from '../../../../../models/ApprovalPersonaProps';
import { ApprovalTemplateCategory, VisibilityPrincipal } from '../../../../../service/gen/templates';
import { getAppHost } from '../../../../../utilities/TeamsUtils';
import { getCategoryOptions } from '../../../../../utilities/TemplateUtils';
import { stringFormat } from '../../../../../utilities/Utils';
import { TemplateIcon } from '../../../../common/TemplateIcon';
import { TemplatePeoplePicker } from '../../../TemplatePeoplePicker';
import { DataSourceType } from '../../../TemplatePeoplePicker/TemplatePeoplePicker.container';
import { getDescriptionStyles, getTitleStyles } from '../../CreateTemplateInstanceWizard.styles';

import {
    getCategoryStyles,
    getIconStyles,
    getNameContainerStyles,
    getStackStyles,
    getStackTokens,
} from './BasicSettings.styles';

export interface BasicSettingsComponentProps {
    iconName: string;
    iconBackgroundColor: string;
    onNameChange?: (errorMessage: string, newValue: string) => void;
    onNameBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    nameError?: string;
    title: string;
    onDescriptionChange?: (errorMessage: string, newValue: string) => void;
    descriptionError?: string;
    description: string;
    onSpecificPeopleChange?: (specificPeopleSelected: IPersonaProps[]) => void;
    specificPeople?: VisibilityPrincipal[];
    specificPeopleError?: string;
    showSpecificPeople?: boolean;
    showScopeSettings: boolean;
    scope: string;
    onScopeSettingsChange?: (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => void;
    category: ApprovalTemplateCategory;
    onCategoryChange?: (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => void;
    onIconClick?: () => void;
    showStatus: boolean;
    status: boolean;
    onStatusChange?: (event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => void;
}

export const BasicSettingsComponent: React.FC<BasicSettingsComponentProps> = (
    props: BasicSettingsComponentProps
): JSX.Element => {
    const loc = getAppHost().getLocale();
    const theme = React.useContext(CustomizerContext).customizations.settings.theme;

    const {
        iconName,
        iconBackgroundColor,
        onNameChange,
        nameError,
        title,
        onDescriptionChange,
        descriptionError,
        description,
        specificPeople,
        specificPeopleError,
        onSpecificPeopleChange,
        showSpecificPeople,
        showScopeSettings,
        onScopeSettingsChange,
        scope,
        onCategoryChange,
        category,
        onIconClick,
        showStatus,
        status,
        onStatusChange,
    } = props;

    const specificPeoplePersonas = specificPeople?.map((principal: VisibilityPrincipal): ApprovalPersonaProps => {
        return { id: principal.PrincipalId, text: principal.DisplayName, personaType: principal.PrincipalType };
    });

    const scopeSettingOptions: IDropdownOption[] = [
        { key: TemplateScope.TenantAll, text: loc.strings.org_wide_option_text },
        { key: TemplateScope.TenantSpecificPeople, text: loc.strings.specific_people_option_text },
    ];

    const statusLabel = status ? loc.strings.enabled_text : loc.strings.disabled_text;

    return (
        <Stack styles={getStackStyles(theme)}>
            <Label
                id='basic-settings-label'
                aria-label={loc.strings.basic_settings_wizard_step_label}
                styles={getTitleStyles(theme)}>
                {loc.strings.basic_settings_wizard_step_label}
            </Label>
            <Label
                id='basic-settings-desc'
                aria-label={loc.strings.basic_settings_wizard_step_description}
                styles={getDescriptionStyles(theme)}>
                {loc.strings.basic_settings_wizard_step_description}
            </Label>
            <Stack verticalAlign='center' tokens={getStackTokens} horizontal styles={getNameContainerStyles}>
                <TemplateIcon
                    styles={getIconStyles()}
                    iconName={iconName}
                    onIconClick={onIconClick}
                    iconBackgroundColor={iconBackgroundColor}
                />
                <TitleTextField
                    label={loc.strings.template_name_label}
                    onValueChange={onNameChange}
                    placeholder={loc.strings.template_name_input_placeholder}
                    errorMessage={nameError}
                    defaultValue={title}
                    maxLength={Constants.maxTemplateTitleCharacterLimit}
                    ariaLabel={loc.strings.template_name_input_aria_label}
                />
            </Stack>

            <Stack className='template-instance-fields'>
                <Dropdown
                    label={loc.strings.category_label}
                    responsiveMode={ResponsiveMode.large}
                    styles={getCategoryStyles()}
                    options={getCategoryOptions()}
                    onChange={onCategoryChange}
                    selectedKey={category}
                />

                <DetailsTextField
                    label={loc.strings.description_label}
                    onValueChange={onDescriptionChange}
                    placeholder={loc.strings.template_description_input_placeholder}
                    errorMessage={descriptionError}
                    defaultValue={description}
                    maxLength={Constants.maxTemplateDetailCharacterLimit}
                    ariaLabel={loc.strings.template_description_input_aria_label}
                />

                {showScopeSettings && (
                    <Dropdown
                        label={loc.strings.scope_settings_label}
                        styles={getCategoryStyles()}
                        responsiveMode={ResponsiveMode.large}
                        options={scopeSettingOptions}
                        onChange={onScopeSettingsChange}
                        selectedKey={scope}
                    />
                )}
                {showSpecificPeople && (
                    <TemplatePeoplePicker
                        label={
                            showScopeSettings
                                ? loc.strings.visibility_scope_column_header
                                : loc.strings.specific_people_option_text
                        }
                        onApproversSelected={onSpecificPeopleChange}
                        error={specificPeopleError}
                        currentSelectedItems={specificPeoplePersonas}
                        dataSourceType={DataSourceType.TenantWidePeopleAndGroup}
                    />
                )}

                {showStatus && (
                    <>
                        <Label className='template-label'>{loc.strings.template_status_label}</Label>
                        <Toggle
                            checked={status}
                            label={statusLabel}
                            inlineLabel
                            ariaLabel={stringFormat(loc.strings.template_status_toggle_aria_label, {
                                status: statusLabel,
                            })}
                            onChange={onStatusChange}
                        />
                    </>
                )}
            </Stack>
        </Stack>
    );
};
